/**
 * Entitlements por rol para el modelo de pago por uso (IT-605).
 *
 * Espejo del mapa de calc-api (src/config/entitlements.js): la fuente de
 * verdad es el backend, que envía `user.entitlements` en login/registro/
 * verify-token. Este módulo aporta los slugs compartidos y un fallback local
 * (por rol) para usuarios guardados en localStorage antes de este cambio.
 * Cualquier cambio de slugs debe replicarse en calc-api.
 */

export const PAYG_ROLE = 'payg';

export const FEATURES = Object.freeze({
  /** Acceso a Geotrim (catálogo de ubicaciones) */
  GEOTRIM: 'geotrim',
  /** Datos de referencia catastral y diseño de instalaciones solares */
  CADASTRAL: 'cadastral',
  /** Fuentes de curva solar distintas de PVGIS (importación real / IA) */
  SOLAR_IMPORT: 'solarImport',
  /** Fuentes de demanda distintas de los datos demo */
  DEMAND_IMPORT: 'demandImport',
  /** Importación de ficheros de precios */
  PRICES_IMPORT: 'pricesImport',
  /** Modelos de operación distintos de autoconsumo */
  OPERATION_MODELS: 'operationModels',
  /** Catálogo de baterías */
  BATTERY_CATALOG: 'batteryCatalog',
  /** Subida y procesado de documentos (extracción IA de PDFs) */
  DOCUMENTS: 'documents',
  /** Visualizaciones completas (más allá de cards y flujos de energía) */
  FULL_RESULTS: 'fullResults',
  /** Funciones de auditoría IA por sección */
  AUDIT: 'audit',
  /** Descarga de datos */
  DATA_DOWNLOAD: 'dataDownload',
  /** Generación de informes */
  REPORTS: 'reports'
});

export const ALL_FEATURES = Object.freeze(Object.values(FEATURES));

const buildEntitlements = (enabled) =>
  ALL_FEATURES.reduce((acc, feature) => {
    acc[feature] = enabled;
    return acc;
  }, {});

/** Fallback local cuando el user no trae entitlements del backend. */
export const getDefaultEntitlements = (role) => {
  if (!role) return buildEntitlements(false);
  return buildEntitlements(role !== PAYG_ROLE);
};

/** Entitlements efectivos de un user (backend primero, fallback por rol). */
export const getUserEntitlements = (user) => {
  if (user?.entitlements && typeof user.entitlements === 'object') {
    return { ...getDefaultEntitlements(user.role), ...user.entitlements };
  }
  return getDefaultEntitlements(user?.role);
};

export const userHasFeature = (user, feature) =>
  getUserEntitlements(user)[feature] === true;

/**
 * Ruta de aterrizaje tras login/registro según el plan (IT-606): el usuario
 * de pago por uso va directo al panel de proyectos, sin pasar por Mi área.
 */
export const getPostAuthRoute = (user) =>
  user?.role === PAYG_ROLE ? '/ecotrim-area' : '/mi-area';
